/**
 * Interfaces for the new Onboarding Flow
 * Three-phase onboarding: Manager Initiation → Employee Data Entry → Manager Review
 */

export enum OnboardingStatus {
	DRAFT = 'draft',
	IN_PROGRESS = 'in_progress',
	EMPLOYEE_PENDING = 'employee_pending',
	MANAGER_REVIEW = 'manager_review',
	COMPLETED = 'completed',
	ARCHIVED = 'archived'
}

export enum EmployeeType {
	NEW = 'new',
	REHIRE = 'rehire',
	EXISTING = 'existing'
}

export enum EmploymentType {
	HOURLY = 'hourly',
	SALARY = 'salary'
}

export enum RoleType {
	PART_TIME = 'part_time',
	FULL_TIME = 'full_time'
}

export enum MaritalStatus {
	SINGLE = 'single',
	MARRIED = 'married',
	DIVORCED = 'divorced',
	WIDOWED = 'widowed'
}

export enum Gender {
	MALE = 'male',
	FEMALE = 'female',
	OTHER = 'other',
	PREFER_NOT_TO_SAY = 'prefer_not_to_say'
}

/**
 * Phase 1 - Manager Initiation Data
 */
export interface IManagerInitiationData {
	employeeType: EmployeeType;
	existingEmployeeId?: string; // If rehire or existing
	firstName: string;
	middleName?: string;
	lastName: string;
	email: string; // Must be unique
	cellPhone: string;
	employmentStartDate: string; // ISO date
	employmentType: EmploymentType;
	roleType: RoleType;
	securityRole: string; // Mabel Security Role IRI
	hierarchyAssignment?: string; // Hierarchy IRI
}

/**
 * Phase 2 - Employee Data Entry
 */
export interface IEmployeeBasicData {
	// Confirming manager-entered data (can be edited by employee)
	firstName: string;
	middleName?: string;
	lastName: string;
	cellPhone: string;
	
	// New employee-entered data
	address1: string;
	address2?: string;
	city: string;
	state: string;
	zip: string;
	dateOfBirth: string; // ISO date
	gender: Gender;
	maritalStatus: MaritalStatus;
}

export interface IAvailabilityData {
	// This will be populated if scheduling is licensed
	// Structure depends on existing availability interface
	availabilitySchedule?: any[];
}

export interface IPDFProgress {
	documentId: string;
	documentName: string;
	documentType: 'data_entry' | 'policy' | 'upload';
	completed: boolean;
	completedAt?: string; // ISO timestamp
	filePath?: string; // Cloud storage path for completed document
}

export interface IEmployeeDataEntry {
	basicData: IEmployeeBasicData;
	availability?: IAvailabilityData;
	pdfProgress: IPDFProgress[];
	phase2CompletedAt?: string; // ISO timestamp when employee finished
}

/**
 * Phase 3 - Manager Review Data
 */
export interface IManagerReview {
	reviewedBy: string; // Manager user IRI
	reviewedAt: string; // ISO timestamp
	attestation: boolean; // Manager attests they reviewed documentation
	completedAt?: string; // ISO timestamp when manager completed onboarding
	posIntegrationSuccess?: boolean; // If POS integration is enabled
	posIntegrationMessage?: string;
}

/**
 * Complete Onboarding Record
 */
export interface IOnboardingRecord {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id?: string;
	workplace: string; // Workplace IRI
	status: OnboardingStatus;
	
	// Phase 1 data
	managerInitiation: IManagerInitiationData;
	initiatedBy: string; // Manager user IRI
	initiatedAt: string; // ISO timestamp
	
	// Phase 2 data
	employeeDataEntry?: IEmployeeDataEntry;
	
	// Phase 3 data
	managerReview?: IManagerReview;
	
	// User reference (created after Phase 1)
	userId?: string; // User IRI
	
	createdAt: string;
	updatedAt: string;
}

/**
 * Dashboard view interfaces
 */
export interface IOnboardingDashboardFilters {
	status?: OnboardingStatus[];
	search?: string;
	dateRange?: {
		from: string;
		to: string;
	};
}

export interface IOnboardingListItem {
	id: string;
	employeeName: string;
	email: string;
	status: OnboardingStatus;
	initiatedAt: string;
	initiatedBy: string;
	currentPhase: 'manager_initiation' | 'employee_data_entry' | 'manager_review' | 'completed';
	progress: number; // Percentage (0-100)
}
